import {
    Paper, Typography, Divider, Box, Grid, TextField, Button, Stack, IconButton,
    Tooltip, Collapse, Chip, Checkbox, FormControlLabel
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import PaymentIcon from "@mui/icons-material/Payment";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import React, { useMemo, useState } from "react";
import CUSTOM_DIALOG from "../../../../main_components/modal/custom_dialog";
// import { revokeSession } from "../../../../main_components/tokens/get_headers_user";

import { money } from "../utils/format";
import { calcularTotalesDeRenglon } from "../utils/invoice";
import { MAX_DEC } from "../constants/config";
import RevealSection from "./InvoiceSummary/RevealSection";
// hooks nuevos
import { useEmailList } from "../hooks/useEmailList";
import { useCurrencyTC } from "../hooks/useCurrencyTC";
import { usePreviewFlow } from "../hooks/usePreviewFlow";
import { useStickyFooterHeight } from "../hooks/useStickyFooterHeight";
import { useIVA } from "../hooks/useIVA";
import { useLocalStorageFlag } from "../hooks/useLocalStorageFlag";

// tipos
import type { InvoiceSummary as InvoiceSummaryProps } from "../types/InvoiceSummary";

const InvoiceSummary = (props: InvoiceSummaryProps) => {
    const {
        productos, onGenerar, onRelacionar, relacionados = [], onEliminarRelacionado,
        relacionarDisabled = false, relacionarTooltip = "",
        metodoPago, setMetodoPago, formaPago, setFormaPago,
        listaMetodoPago, listaFormaPago, moneda, setMoneda,
        tipoCambio, setTipoCambio, listaMonedas,
        diasCredito, setDiasCredito, tipoCE, serie, folio,
        cliente, correosFijos, correosExtra, onChangeCorreosExtra,
        onAplicarIVA, hasProductoSeleccionado, canGenerar = true, generarTooltip = "",
        onGuardarBorrador,           // <-- NUEVO
        onAbrirBorradores,
    } = props;

    // ——— resumen
    const resumen = useMemo(() =>
        productos.reduce(
            (acc, p) => {
                const t = calcularTotalesDeRenglon(p);
                acc.subtotal += t.subtotal;
                acc.descuentos += t.descuento;
                acc.impuestosFederales += t.impuestosFederales;
                acc.impuestosLocales += t.impuestosLocales;
                acc.total += t.total;
                return acc;
            },
            { subtotal: 0, descuentos: 0, impuestosFederales: 0, impuestosLocales: 0, total: 0 }
        ), [productos]
    );

    // ——— emails
    const {
        fijos, extra, correosCombinados,
        correoActual, setCorreoActual, emailError, setEmailError,
        tryAddEmail, handleEliminarCorreo, handleKeyDownEmail
    } = useEmailList({ correosFijos, correosExtra, onChangeCorreosExtra });

    // ——— moneda / TC
    const { tcInput, tcInvalido, handleChangeTC, handleBlurTC } =
        useCurrencyTC({ moneda, tipoCambio, setTipoCambio, MAX_DEC });

    // ——— calc IVA
    const { showCalcIVA, setShowCalcIVA, precioConIVA, setPrecioConIVA, aplicarIVA } =
        useIVA(hasProductoSeleccionado, onAplicarIVA);

    // ——— preview y secuencia
    const [previewOpen, setPreviewOpen] = useState(false);
    const {
        showAll, setShowAll, maxPreview, hayMas,
        hasSeenEnd, contentRef, endRef, handleScrollPreview, scrollToEnd,
        step, goNext, sectionRefs, scrollToSection,
        cuSubtotal, cuImpFed, cuImpLoc, cuTotal,
        confirmarDisabledBase, confirmarDisabled
    } = usePreviewFlow({
        previewOpen, setPreviewOpen, productosLength: productos.length,
        resumen, moneda, metodoPago, diasCredito: diasCredito as number | ""
    });

    // ——— footer sticky
    const { footerRef, footerH } = useStickyFooterHeight(previewOpen);

    // ——— omit preview
    const { flag: skipPreview, setFlag: setSkipPreview } = useLocalStorageFlag("invoice_skip_preview");

    const visibles = showAll ? productos : productos.slice(0, maxPreview);
    const tipoLabel = tipoCE === "I" ? "INGRESO" : "EGRESO";
    const correosParaMostrar = correosCombinados;

    const handleClickGenerar = () => {
        if (!canGenerar) return;
        if (metodoPago === "PPD") {
            const d = Number(diasCredito);
            if (!Number.isFinite(d) || d <= 0) { alert("Indica los días de crédito para PPD (entero mayor a 0)."); return; }
        }
        if (skipPreview) { onGenerar(); return; }
        setPreviewOpen(true);
    };

    const handleConfirmar = () => { setPreviewOpen(false); onGenerar(); };

    const sxInputHeight = { "& .MuiInputBase-root": { height: 40 } };
    const [showCurrency, setShowCurrency] = useState(false);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

            {/* Botones superiores */}
            <Stack direction="row" spacing={1}>
                <Tooltip title={relacionarTooltip} disableHoverListener={!relacionarDisabled}>
                    <span style={{ display: "inline-block", width: "100%" }}>
                        <Button fullWidth variant="contained" startIcon={<LinkIcon />} onClick={onRelacionar}
                            disabled={relacionarDisabled}
                            sx={{ backgroundColor: "blue", "&:hover": { backgroundColor: "#0a42abff" }, textTransform: "none", fontWeight: 600 }}>
                            RELACIONAR
                        </Button>
                    </span>
                </Tooltip>

                <Tooltip title={hasProductoSeleccionado ? "" : "Selecciona un producto para usar esta opción"}>
                    <span style={{ display: "inline-block", width: "100%" }}>
                        <Button fullWidth variant="contained" startIcon={<CalculateOutlinedIcon />}
                            onClick={() => hasProductoSeleccionado && setShowCalcIVA(v => !v)}
                            disabled={!hasProductoSeleccionado}
                            sx={{ backgroundColor: "blue", "&:hover": { backgroundColor: "#5a24b8" }, textTransform: "none", fontWeight: 600 }}>
                            CALCULAR IVA
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            {/* Calc IVA */}
            <Collapse in={showCalcIVA} unmountOnExit>
                <Box sx={{ mt: 1.25, display: "flex", gap: 1 }}>
                    <TextField
                        label="Precio con IVA"
                        placeholder="0.00"
                        size="medium"
                        fullWidth
                        value={precioConIVA}
                        onChange={(e) => {
                            // mismo saneo que tenías (6 decimales)
                            let v = e.target.value.replace(/[^\d.]/g, "");
                            const firstDot = v.indexOf(".");
                            if (firstDot !== -1) {
                                v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
                                const [ent, dec = ""] = v.split(".");
                                v = dec.length > 6 ? `${ent}.${dec.slice(0, 6)}` : v;
                            }
                            setPrecioConIVA(v);
                        }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); aplicarIVA(); } }}
                        InputProps={{ inputMode: "decimal" }}
                        helperText="Ingresa el precio final (con IVA)."
                    />
                    <Button onClick={aplicarIVA} variant="contained"
                        disabled={!precioConIVA || !Number.isFinite(Number(precioConIVA)) || Number(precioConIVA) <= 0}
                        sx={{ height: 40, textTransform: "none", fontWeight: 700, px: 2, borderRadius: 1 }}>
                        Aplicar
                    </Button>
                </Box>
            </Collapse>

            {/* CFDI relacionados */}
            <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
                <Box display="flex" alignItems="center" mb={1}>
                    <LinkIcon sx={{ color: "blue", mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600}>CFDI Relacionados</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                {relacionados.length === 0 ? (
                    <Typography variant="subtitle2" fontWeight={700}>No hay CFDI relacionados</Typography>
                ) : (
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                        {relacionados.map((r) => (
                            <Chip key={r.uuid} label={`Tipo ${r.tiporelacion} – ${r.uuid}`}
                                onDelete={onEliminarRelacionado ? () => onEliminarRelacionado(r.uuid) : undefined}
                                size="small" variant="outlined" deleteIcon={<DeleteOutlineIcon />} />
                        ))}
                    </Stack>
                )}
            </Paper>

            {/* Correos */}
            <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
                <Box display="flex" alignItems="center" mb={1}>
                    <EmailOutlinedIcon sx={{ color: "#c2a237ff", mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600}>Correos a Enviar</Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                        value={correoActual}
                        onChange={(e) => { setCorreoActual(e.target.value); if (emailError) setEmailError(null); }}
                        onKeyDown={handleKeyDownEmail}
                        placeholder="ejemplo@ejemplo.com"
                        size="medium"
                        fullWidth
                        error={!!emailError}
                        helperText={emailError || ""}
                    />
                    <IconButton aria-label="Agregar correo" onClick={tryAddEmail}
                        disabled={!correoActual || !!emailError}
                        sx={{ border: "1px solid #e0e0e0", width: 35, height: 35, borderRadius: "8px" }}>
                        <AddIcon />
                    </IconButton>
                </Stack>

                {correosCombinados.length ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                        {fijos.map((c) => (<Chip key={`fijo:${c}`} label={c} size="small" variant="outlined" />))}
                        {extra.map((c) => (
                            <Chip key={`extra:${c}`} label={c} onDelete={() => handleEliminarCorreo(c)}
                                size="small" variant="outlined" deleteIcon={<DeleteOutlineIcon sx={{ color: "red" }} />} />
                        ))}
                    </Box>
                ) : null}
            </Paper>

            {/* Información de Pago */}
            <Paper elevation={1} sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <PaymentIcon sx={{ color: "blue" }} />
                        <Typography variant="subtitle1" fontWeight={600}>Información de Pago</Typography>
                    </Box>
                    <Tooltip title={showCurrency ? "Ocultar opciones de moneda" : "Mostrar opciones de moneda"}>
                        <MonetizationOnOutlinedIcon fontSize="medium" sx={{ color: "orange", cursor: "pointer" }}
                            titleAccess="Cambiar tipo de moneda" onClick={() => setShowCurrency(v => !v)} />
                    </Tooltip>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Autocomplete
                            options={listaMetodoPago}
                            value={listaMetodoPago.find((o) => o.value === metodoPago) || null}
                            onChange={(_, v) => setMetodoPago(v?.value || "")}
                            getOptionLabel={(o) => o.label}
                            isOptionEqualToValue={(o, v) => o.value === v.value}
                            fullWidth disablePortal size="small"
                            renderInput={(params) => <TextField {...params} label="Método de Pago" size="small" sx={sxInputHeight} />}
                        />
                    </Grid>

                    {metodoPago === "PPD" && (
                        <Grid item xs={12}>
                            <TextField
                                label="Días de crédito" type="number" size="small" fullWidth sx={sxInputHeight}
                                value={diasCredito}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === "") { setDiasCredito(""); return; }
                                    const n = Math.max(0, Math.floor(Number(v)));
                                    setDiasCredito(Number.isFinite(n) ? n : "");
                                }}
                                inputProps={{ min: 1, step: 1 }}
                                placeholder="Ej. 30" helperText="Requerido cuando el método es PPD"
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Autocomplete
                            options={listaFormaPago}
                            value={listaFormaPago.find((o) => o.value === formaPago) || null}
                            onChange={(_, v) => setFormaPago(v?.value || "")}
                            getOptionLabel={(o) => o.label}
                            isOptionEqualToValue={(o, v) => o.value === v.value}
                            fullWidth disablePortal size="small"
                            renderInput={(params) => <TextField {...params} label="Forma de Pago" size="small" sx={sxInputHeight} />}
                        />
                    </Grid>
                </Grid>

                <Collapse in={showCurrency} unmountOnExit>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                options={listaMonedas}
                                value={listaMonedas.find((o) => o.value === moneda) || null}
                                onChange={(_, v) => setMoneda(v?.value || "MXN")}
                                getOptionLabel={(o) => o.label}
                                isOptionEqualToValue={(o, v) => o.value === v.value}
                                fullWidth disablePortal size="small"
                                renderInput={(params) => <TextField {...params} label="Moneda" size="small" sx={sxInputHeight} />}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Tipo de cambio" type="text" size="small" fullWidth sx={sxInputHeight}
                                value={moneda === "MXN" ? "1.000000" : tcInput}
                                onChange={handleChangeTC} onBlur={handleBlurTC}
                                disabled={moneda === "MXN"} error={tcInvalido}
                                helperText={moneda === "MXN" ? "" : tcInvalido ? "Debe ser > 0 y ≠ 1; máx. 6 dec." : " "}
                                inputProps={{ inputMode: "decimal" }}
                            />
                        </Grid>
                    </Grid>
                </Collapse>
            </Paper>

            {/* Resumen mini */}
            <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center">
                        <RequestQuoteOutlinedIcon sx={{ color: "#6C2BD9", mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight={600}>Resumen de Factura</Typography>
                    </Box>
                    <FormControlLabel
                        control={<Checkbox checked={skipPreview} onChange={(_, v) => setSkipPreview(v)} size="small" />}
                        label="Omitir Resumen"
                        sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.85rem", color: "text.secondary" } }}
                    />
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2">{money(resumen.subtotal, moneda)}</Typography>
                </Box>

                {resumen.descuentos > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">Descuentos:</Typography>
                        <Typography variant="body2">-{money(resumen.descuentos, moneda)}</Typography>
                    </Box>
                )}

                <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Impuestos:</Typography>
                    <Typography variant="body2">{money(resumen.impuestosFederales, moneda)}</Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Impuestos Locales:</Typography>
                    <Typography variant="body2">{money(resumen.impuestosLocales, moneda)}</Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography variant="subtitle1" fontWeight={700}>Total:</Typography>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#6C2BD9" }}>
                        {money(resumen.total, moneda)}
                    </Typography>
                </Box>

                {moneda !== "MXN" && (
                    <Typography variant="caption" color="text.secondary">
                        Mostrando importes en {moneda}{tipoCambio > 0 ? ` (TC: ${tipoCambio.toFixed(6)})` : ""}
                    </Typography>
                )}

                <Tooltip title={canGenerar ? "" : (generarTooltip || "Completa los campos requeridos")}>
                    <span style={{ display: "inline-block", width: "100%" }}>
                        <Button fullWidth variant="contained" startIcon={<ArticleOutlinedIcon />}
                            onClick={handleClickGenerar} disabled={!canGenerar}
                            sx={{ fontWeight: 700, textTransform: "uppercase", mt: 1.5 }}>
                            {generarTooltip}
                        </Button>
                    </span>
                </Tooltip>

                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={onGuardarBorrador}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                        Guardar para después
                    </Button>

                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={onAbrirBorradores}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                        Borradores
                    </Button>
                </Stack>

                {/* <Button fullWidth variant="contained" startIcon={<ArticleOutlinedIcon />}
                    onClick={() => revokeSession()} sx={{ fontWeight: 700, textTransform: "uppercase", mt: 1.5 }}>
                    revokeSession
                </Button> */}
            </Paper>

            {/* PREVIEW */}
            <CUSTOM_DIALOG
                title="Revisa cuidadosamente la información antes de generar la factura"
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                size="xl"
                component={
                    <Box sx={{ bgcolor: "#F7F8FA", borderRadius: 2, maxHeight: 640, overflow: "auto" }}
                        ref={contentRef} onScroll={handleScrollPreview}>
                        {/* ——— Grid principal (idéntico a tu código) */}
                        <Box sx={{
                            p: 2, pt: 1, display: "grid", gap: 1.25,
                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                            gridTemplateAreas: { xs: `"a" "b" "c" "d" "e" "f"`, md: `"a b" "c d" "e e" "f f"` }
                        }}>
                            {/* a) Información del Comprobante */}
                            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #E6E8EE", bgcolor: "#fff", gridArea: "a" }}
                                ref={el => sectionRefs.current[0] = el}>
                                <RevealSection title="Información del Comprobante" start={step === 0} onDone={goNext}
                                    scrollIntoView={() => scrollToSection(0)}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}><Typography variant="caption" color="text.secondary">Tipo</Typography><Typography fontWeight={700}>{tipoLabel}</Typography></Grid>
                                        <Grid item xs={4}><Typography variant="caption" color="text.secondary">Serie</Typography><Typography fontWeight={700}>{serie || "—"}</Typography></Grid>
                                        <Grid item xs={4}><Typography variant="caption" color="text.secondary">Folio</Typography><Typography fontWeight={700}>{folio || "—"}</Typography></Grid>
                                    </Grid>
                                </RevealSection>
                            </Paper>

                            {/* b) Información del Cliente */}
                            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #E6E8EE", bgcolor: "#fff", gridArea: "b" }}
                                ref={el => sectionRefs.current[1] = el}>
                                <RevealSection title="Información del Cliente" start={step === 1} onDone={goNext}
                                    scrollIntoView={() => scrollToSection(1)}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}><Typography variant="caption" color="text.secondary">Cliente</Typography><Typography fontWeight={700}>{cliente?.razonSocial || "—"}</Typography></Grid>
                                        <Grid item xs={12} sm={4}><Typography variant="caption" color="text.secondary">Uso de CFDI</Typography><Typography>{cliente?.usoCFDI || "—"}</Typography></Grid>
                                        <Grid item xs={12} sm={4}><Typography variant="caption" color="text.secondary">Régimen Fiscal</Typography><Typography>{cliente?.regimen || "—"}</Typography></Grid>
                                    </Grid>
                                </RevealSection>
                            </Paper>

                            {/* c) Información de Pago */}
                            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #E6E8EE", bgcolor: "#fff", gridArea: "c" }}
                                ref={el => sectionRefs.current[2] = el}>
                                <RevealSection title="Información de Pago" start={step === 2} onDone={goNext}
                                    scrollIntoView={() => scrollToSection(2)}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}><Typography variant="caption" color="text.secondary">Método de Pago</Typography>
                                            <Typography fontWeight={700}>{metodoPago === "PPD" ? "CRÉDITO (PPD)" : "CONTADO (PUE)"}{metodoPago === "PPD" && (diasCredito ? ` — ${diasCredito} días` : "")}</Typography></Grid>
                                        <Grid item xs={12} md={6}><Typography variant="caption" color="text.secondary">Forma de Pago</Typography>
                                            <Typography>{listaFormaPago.find(o => o.value === formaPago)?.label || formaPago || "—"}</Typography></Grid>
                                        <Grid item xs={12} md={6}><Typography variant="caption" color="text.secondary">Moneda</Typography>
                                            <Typography>{listaMonedas.find(o => o.value === moneda)?.label || moneda}{moneda !== "MXN" && tipoCambio ? ` (TC ${tipoCambio.toFixed(6)})` : ""}</Typography></Grid>
                                        <Grid item xs={12} md={6}><Typography variant="caption" color="text.secondary">Condiciones de Pago</Typography>
                                            <Typography>{metodoPago === "PPD" ? (diasCredito ? `${diasCredito} días` : "—") : "PUE"}</Typography></Grid>
                                    </Grid>
                                </RevealSection>
                            </Paper>

                            {/* d) Correo de Envío */}
                            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #E6E8EE", bgcolor: "#fff", gridArea: "d" }}
                                ref={el => sectionRefs.current[3] = el}>
                                <RevealSection title="Correo de Envío" start={step === 3} onDone={goNext}
                                    scrollIntoView={() => scrollToSection(3)}>
                                    {correosParaMostrar?.length ? (
                                        <Stack direction="row" spacing={1} flexWrap="wrap">{correosParaMostrar.map((c) => <Chip key={c} size="small" label={c} />)}</Stack>
                                    ) : (<Typography color="text.secondary">Sin correos seleccionados</Typography>)}
                                </RevealSection>
                            </Paper>

                            {/* e) Productos y Servicios */}
                            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #E6E8EE", bgcolor: "#fff", gridArea: "e" }}
                                ref={el => sectionRefs.current[4] = el}>
                                <RevealSection
                                    title={`Productos y Servicios (${productos.length})`}
                                    start={step === 4} forceShown={step > 4} onDone={goNext}
                                    scrollIntoView={() => scrollToSection(4)}
                                >
                                    <Stack spacing={1}>
                                        {visibles.map((p, idx) => {
                                            const t = calcularTotalesDeRenglon(p);
                                            return (
                                                <Paper key={idx} variant="outlined" sx={{ p: 1.25, borderRadius: 2, borderColor: "#E6E8EE", bgcolor: "#FAFAFB" }}>
                                                    <Typography fontWeight={700} mb={0.5}>
                                                        {p.descripcion || p?.concepto?.descripcion || p?.concepto?.concept}
                                                    </Typography>
                                                    <Grid container>
                                                        <Grid item xs={3}><Typography variant="caption" color="text.secondary">Cantidad</Typography><Typography>{p.cantidad}</Typography></Grid>
                                                        <Grid item xs={3}><Typography variant="caption" color="text.secondary">Precio Unit.</Typography><Typography>{money(Number(p.precio || p?.concepto?.valor_unitario || 0), moneda)}</Typography></Grid>
                                                        <Grid item xs={3}><Typography variant="caption" color="text.secondary">Impuestos</Typography><Typography>{money(t.impuestosFederales + t.impuestosLocales, moneda)}</Typography></Grid>
                                                        <Grid item xs={3}><Typography variant="caption" color="text.secondary">Total</Typography><Typography fontWeight={700} color="#10B981">{money(t.total, moneda)}</Typography></Grid>
                                                    </Grid>
                                                </Paper>
                                            );
                                        })}
                                    </Stack>

                                    {hayMas && (
                                        <Box mt={1}>
                                            <Button size="small" onClick={() => setShowAll((s) => !s)} sx={{ textTransform: "none" }}>
                                                {showAll ? "Ocultar" : `Ver todos los productos (${productos.length - maxPreview} más)`}
                                            </Button>
                                        </Box>
                                    )}
                                </RevealSection>
                            </Paper>

                            {/* f) Resumen Financiero */}
                            <Paper elevation={0} sx={{ p: 2, border: "2px solid #111", borderRadius: 3, bgcolor: "#fff", gridArea: "f" }}
                                ref={el => sectionRefs.current[5] = el}>
                                <RevealSection title="Resumen Financiero" start={step === 5} onDone={() => goNext()}
                                    scrollIntoView={() => scrollToSection(5)}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                        <Typography color="text.secondary">Subtotal:</Typography>
                                        <Typography>{money(cuSubtotal.val, moneda)}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                        <Typography color="text.secondary">Impuestos:</Typography>
                                        <Typography>{money(cuImpFed.val, moneda)}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                        <Typography color="text.secondary">Impuestos Locales:</Typography>
                                        <Typography>{money(cuImpLoc.val, moneda)}</Typography>
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <Typography sx={{ fontWeight: 800 }}>Total:</Typography>
                                        <Typography sx={{ fontWeight: 800, fontSize: 20 }}>{money(cuTotal.val, moneda)}</Typography>
                                    </Box>
                                </RevealSection>
                            </Paper>
                        </Box>

                        <Box sx={{ height: footerH + 16 }} />
                        <Box ref={endRef} sx={{ height: footerH + 16 }} />

                        {/* Barra de acciones */}
                        <Box ref={footerRef}
                            sx={{ position: "sticky", bottom: 0, p: 2, bgcolor: "#fff", borderTop: "1px solid #E6E8EE", display: "flex", gap: 1.25 }}>
                            <Button variant="outlined" onClick={() => setPreviewOpen(false)}
                                sx={{ textTransform: "none", borderRadius: 2, borderColor: "#CBD5E1" }}>
                                Modificar Factura
                            </Button>

                            {!hasSeenEnd ? (
                                <Tooltip title="Desplázate al final para habilitar la confirmación">
                                    <span style={{ marginLeft: "auto" }}>
                                        <Button onClick={scrollToEnd} endIcon={<span style={{ fontSize: 18 }}>↓</span>}
                                            sx={{
                                                textTransform: "none", borderRadius: 2, fontWeight: 700, ml: "auto",
                                                backgroundColor: "#e5e7eb", "&:hover": { backgroundColor: "#d1d5db" }, color: "#111827"
                                            }}
                                            variant="contained">
                                            Confirmar y Generar Factura
                                        </Button>
                                    </span>
                                </Tooltip>
                            ) : (
                                <Tooltip title={confirmarDisabledBase ? "Completa los días de crédito (PPD) para continuar" : ""}>
                                    <span style={{ marginLeft: "auto" }}>
                                        <Button variant="contained" onClick={() => { /* mismos efectos */ }}
                                            startIcon={<CheckCircleOutlineIcon />} disabled={confirmarDisabledBase}
                                            onClickCapture={() => handleConfirmar()}
                                            sx={{
                                                textTransform: "none", borderRadius: 2, backgroundColor: "#2563EB",
                                                "&:hover": { backgroundColor: "#1D4ED8" }, fontWeight: 700
                                            }}>
                                            Confirmar y Generar Factura
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                }
            />
        </Box>
    );
};

export default InvoiceSummary;
